import React, { useLayoutEffect, useMemo, useRef } from 'react';
import { layoutDayColumnAppointments } from '../../calendar/dayGridLayout';
import { cn } from '../../lib/cn';
import { PROVIDERS } from '../../calendar/mockData';
import type { Appointment, CalendarTimeDensity, Provider } from '../../calendar/types';
import { AppointmentCell } from './AppointmentCell';

export interface CalendarGridProps {
  appointments: Appointment[];
  providers?: Provider[];
  large?: boolean;
  timeDensity?: CalendarTimeDensity;
  onSelectAppointment?: (appt: Appointment) => void;
  className?: string;
}

/** Height of one hour block, and the minute marks drawn inside it, per density. */
const TIME_DENSITY: Record<CalendarTimeDensity, { rowHeight: number; marks: number[] }> = {
  Small: { rowHeight: 88, marks: [] },
  Medium: { rowHeight: 132, marks: [30] },
  Large: { rowHeight: 176, marks: [15, 30, 45] },
};

/** Detailed appointment cells need at least this much room per hour. */
const LARGE_CELL_MIN_ROW_HEIGHT = 144;

export function CalendarGrid({
  appointments,
  providers: providersProp,
  large,
  timeDensity = 'Small',
  onSelectAppointment,
  className,
}: CalendarGridProps) {
  const columns = providersProp?.length ? providersProp : PROVIDERS;
  const scrollRef = useRef<HTMLDivElement>(null);
  const startHour = 8;
  const endHour = 19;
  const hours = Array.from({ length: endHour - startHour + 1 }, (_, i) => startHour + i);
  const { rowHeight: densityRowHeight, marks } = TIME_DENSITY[timeDensity];
  const rowHeight = large ? Math.max(densityRowHeight, LARGE_CELL_MIN_ROW_HEIGHT) : densityRowHeight;
  const colWidth = large ? 224 : 190;
  const timeCol = 60;
  const gridBodyHeight = hours.length * rowHeight;
  const nowHour = 14.5;
  const nowOffset = (nowHour - startHour) * rowHeight;
  const laneGap = 4;

  const layoutsByColumn = useMemo(() => {
    const map = new Map<string, ReturnType<typeof layoutDayColumnAppointments<Appointment>>>();
    columns.forEach((p) => {
      map.set(
        p.id,
        layoutDayColumnAppointments(appointments.filter((a) => a.providerId === p.id)),
      );
    });
    return map;
  }, [appointments, columns]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const target = Math.max(0, nowOffset - el.clientHeight * 0.38);
    el.scrollTop = target;
  }, [nowOffset, rowHeight]);

  return (
    <div
      className={cn(
        'cal-fade flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-2)]',
        className,
      )}
      style={{ animationDelay: '150ms' }}
    >
      <div className="calendar-header-band flex shrink-0">
        <div className="shrink-0" style={{ width: timeCol }} />
        {columns.map((p) => {
          return (
            <div
              key={p.id}
              className="relative shrink-0 border-l border-[var(--calendar-header-line)] px-[13px] py-3"
              style={{ width: colWidth }}
            >
              <span
                className="absolute left-[13px] right-[13px] top-0 h-[2.5px] rounded-b-[3px]"
                style={{ background: p.color }}
              />
              <div className="flex items-center gap-2">
                <span
                  className="inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white"
                  style={{ background: p.color, boxShadow: `0 2px 8px -2px ${p.color}` }}
                >
                  {p.initials}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[13px] font-semibold tracking-[-0.01em] text-[var(--calendar-header-ink)]">
                    {p.name}
                  </div>
                  <div className="text-[10.5px] text-[var(--calendar-header-muted)]">{p.role}</div>
                </div>
              </div>
              <div className="mt-2 flex items-center gap-2 text-[10.5px]">
                <span className="font-[var(--font-mono)] font-semibold text-[var(--calendar-header-metric)]">
                  ${(p.production / 1000).toFixed(1)}k
                </span>
                <span className="text-[var(--calendar-header-faint)]">·</span>
                <span className="text-[var(--calendar-header-muted)]">
                  <span className="font-[var(--font-mono)] font-semibold text-[var(--calendar-header-metric)]">{p.patients}</span> pts
                </span>
                <span className="text-[var(--calendar-header-faint)]">·</span>
                <span className="font-[var(--font-mono)] font-semibold text-[var(--calendar-header-metric)]">
                  {p.ontime}%
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div ref={scrollRef} className="cal-scroll relative min-h-0 flex-1 overflow-auto">
        <div className="relative flex" style={{ minHeight: gridBodyHeight }}>
          <div
            className="sticky left-0 z-[2] shrink-0 border-r border-[var(--cream-2)] bg-[var(--cream-1)]"
            style={{ width: timeCol }}
          >
            {hours.map((h, i) => (
              <div
                key={h}
                className={cn(
                  'relative pr-2.5 pt-1 text-right',
                  i < hours.length - 1 && 'border-b border-[var(--cream-2)]',
                )}
                style={{ height: rowHeight }}
              >
                <span className="font-[var(--font-mono)] text-[10px] font-medium text-[var(--ink-4)]">
                  {h > 12 ? h - 12 : h} {h >= 12 ? 'PM' : 'AM'}
                </span>
                {marks.map((m) => (
                  <span
                    key={m}
                    className="absolute right-2.5 -translate-y-1/2 font-[var(--font-mono)] text-[9px] text-[var(--ink-4)] opacity-70"
                    style={{ top: `${String((m / 60) * 100)}%` }}
                  >
                    :{m}
                  </span>
                ))}
              </div>
            ))}
          </div>
          {columns.map((p) => (
            <div
              key={p.id}
              className="relative shrink-0 border-l border-[var(--cream-2)]"
              style={{ width: colWidth, minHeight: gridBodyHeight }}
            >
              {hours.map((h, i) => (
                <div
                  key={h}
                  className={cn(
                    'relative',
                    i < hours.length - 1 && 'border-b border-[color-mix(in_srgb,var(--cream-2)_60%,transparent)]',
                  )}
                  style={{ height: rowHeight }}
                >
                  {marks.map((m) => (
                    <div
                      key={m}
                      className="absolute inset-x-0 border-t border-dashed border-[color-mix(in_srgb,var(--cream-2)_60%,transparent)]"
                      style={{ top: `${String((m / 60) * 100)}%` }}
                    />
                  ))}
                </div>
              ))}
              {layoutsByColumn.get(p.id)?.map((layout) => {
                const laneWidthPct = 100 / layout.laneCount;
                const laneLeftPct = layout.lane * laneWidthPct;
                return (
                  <div
                    key={layout.id}
                    className="absolute z-[1] box-border min-w-0"
                    style={{
                      top: (layout.hour - startHour) * rowHeight + 3,
                      left: `${laneLeftPct}%`,
                      width: `${laneWidthPct}%`,
                      paddingLeft: laneGap / 2,
                      paddingRight: laneGap / 2,
                    }}
                  >
                    <AppointmentCell
                      appt={layout.item}
                      rowHeight={rowHeight}
                      {...(large !== undefined ? { large } : {})}
                      onClick={() => onSelectAppointment?.(layout.item)}
                    />
                  </div>
                );
              })}
            </div>
          ))}
          <div
            className="pointer-events-none absolute z-[3] h-[1.5px] bg-[var(--current-time)]"
            style={{ top: nowOffset, left: timeCol, right: 0 }}
          >
            <span className="absolute -left-1.5 -top-[5px] h-2.5 w-2.5 rounded-full border-2 border-[var(--cream-1)] bg-[var(--current-time)]" />
            <span className="absolute left-2 -top-2 rounded bg-[var(--current-time)] px-1.5 py-px font-[var(--font-mono)] text-[9px] font-bold tracking-[0.06em] text-white">
              NOW · 2:30
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
